import { z } from 'zod';
import { roleSchema } from './roles';

/**
 * Org types drive labels, defaults and report templates only. No permission
 * check may branch on the type: a university is just an org.
 */
export const ORG_TYPES = [
  'nirvik',
  'university',
  'enterprise',
  'school',
  'government',
  'nonprofit',
  'community',
] as const;
export const orgTypeSchema = z.enum(ORG_TYPES);
export type OrgType = z.infer<typeof orgTypeSchema>;

/** Types a user may create. `nirvik` orgs are platform-managed. */
export const USER_CREATABLE_ORG_TYPES = ORG_TYPES.filter((t) => t !== 'nirvik');

/**
 * What happens when someone signs in with an email on a verified domain.
 * - open:        auto-join as learner
 * - approval:    a join request lands in the org admins' queue
 * - invite_only: the domain only suggests the org; joining needs an invite
 * - managed:     the org controls the account lifecycle (enterprise, with SSO; later)
 */
export const JOIN_POLICIES = ['open', 'approval', 'invite_only', 'managed'] as const;
export const joinPolicySchema = z.enum(JOIN_POLICIES);
export type JoinPolicy = z.infer<typeof joinPolicySchema>;

/**
 * An org tree is at most three levels deep (e.g. university → affiliated
 * college → department). Depth 0 is a top-level org.
 */
export const MAX_ORG_DEPTH = 2;

/**
 * Depth of a new child given its parent's depth. Throws when the child
 * would exceed the three-level cap.
 */
export function childDepth(parentDepth: number): number {
  const depth = parentDepth + 1;
  if (depth > MAX_ORG_DEPTH) {
    throw new OrgDepthError(MAX_ORG_DEPTH + 1);
  }
  return depth;
}

export class OrgDepthError extends Error {
  constructor(maxLevels: number) {
    super(`Org trees are limited to ${maxLevels} levels`);
    this.name = 'OrgDepthError';
  }
}

/** ISO 3166-1 alpha-2 country code, upper-case. */
export const countryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Za-z]{2}$/)
  .transform((c) => c.toUpperCase());

/** Display names for org types (UI and emails). */
export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  nirvik: 'Nirvik',
  university: 'University',
  enterprise: 'Enterprise',
  school: 'School',
  government: 'Government',
  nonprofit: 'Nonprofit',
  community: 'Community',
};

/** Member activity in the org's tree: active in the last 30 days, quiet up to 90, inactive after that (or never). */
export const MEMBER_ACTIVITY = ['active', 'quiet', 'inactive'] as const;
export type MemberActivity = (typeof MEMBER_ACTIVITY)[number];
export const MEMBER_SORTS = ['name', 'joined', 'active'] as const;
export const MEMBERS_PAGE_SIZE = 25;

export const memberQuerySchema = z.object({
  /** Name or email. */
  q: z.string().trim().max(100).optional(),
  role: roleSchema.optional(),
  activity: z.enum(MEMBER_ACTIVITY).optional(),
  sort: z.enum(MEMBER_SORTS).default('name'),
  /** Members of child orgs too (parent-org view). */
  /** Members of this group (of the org). */
  groupId: z.string().uuid().optional(),
  descendants: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(MEMBERS_PAGE_SIZE),
});
export type MemberQuery = z.infer<typeof memberQuerySchema>;

/** Platform admins' user list: the same paging and activity bands, across the platform. */
export const ADMIN_USER_STATUSES = ['admin', 'creator', 'suspended'] as const;
export type AdminUserStatus = (typeof ADMIN_USER_STATUSES)[number];
export const ADMIN_USER_SORTS = ['joined', 'name', 'active'] as const;
export const adminUserQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  /** Members of this org or any org under it. */
  orgId: z.string().uuid().optional(),
  status: z.enum(ADMIN_USER_STATUSES).optional(),
  activity: z.enum(MEMBER_ACTIVITY).optional(),
  sort: z.enum(ADMIN_USER_SORTS).default('joined'),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(MEMBERS_PAGE_SIZE),
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;

// ─── Groups ─────────────────────────────────────────────────────────────

/** Groups split an org's members (a batch, a department, a team) for assigning work and reports. */
export const groupInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullable().optional(),
});
export type GroupInput = z.infer<typeof groupInputSchema>;

export const groupMembersInputSchema = z.object({
  userIds: z.array(z.string().uuid()).min(1).max(500),
});

// ─── Email invitations ──────────────────────────────────────────────────

/** Days an emailed invitation stays valid; resending starts the clock again. */
export const INVITATION_DAYS = 14;
export const MAX_INVITES_PER_SEND = 100;

/** Splits pasted text ("a@x.org, b@y.org\nc@z.org") into addresses. */
export const splitEmails = (text: string) => [
  ...new Set(
    text
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean),
  ),
];

export const invitationInputSchema = z.object({
  emails: z
    .array(z.string().trim().toLowerCase().email().max(254))
    .min(1)
    .max(MAX_INVITES_PER_SEND),
  /** Ownership is never handed out by invitation. */
  role: roleSchema.exclude(['owner']).default('learner'),
  groupId: z.string().uuid().nullable().optional(),
  message: z.string().trim().max(500).nullable().optional(),
});
export type InvitationInput = z.infer<typeof invitationInputSchema>;
